import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import RefLink from '../shared/RefLink';
import { coverageMeta, coverageBadgeAdded, coverageBadgeRemoved, coverageList, coverageListRow, fmtAmendValue, nameNow, rowExpandToggle } from './constants';
import { NOW_TITLE } from '../shared/NowName';
import { engagementNo } from '../../utils/engagementNo';
import { parseEngagementName, engagementTag } from '../../utils/engagementName';
import { formatDateShort } from '../../utils/dateFormat';

// 組合案名與「多筆 engagement」的統一顯示
// （「A - UEN - FYE… + B - UEN - FYE… + C - …」串成一行完全讀不動）。
//
// 資料層不動：DB 裡的 payload 字串、稽核事件、信件仍然是組合名（那是稽核
// 軌跡）。這裡只管顯示——
//   * 公司名當主體，UEN/FYE 降成淡色小字（EngagementName）
//   * 多筆一律一筆一行（EngagementNameList），絕不再用「 + 」串
//   * 只有字串可用時（History 差異表）就地拆回清單（splitJoinedNames）
//   * 涵蓋差異加 Added / Removed 徽章（AddedBadge / RemovedBadge）
// 觀感 token 一律在 constants.js（coverageMeta / coverageBadge*）。

// 「Entity Name - UEN - FYE31-12-2025 - Service #n」→ { entity, uen, fye,
// service, seq }（規則單一出處 utils/engagementName：認 FYE 那一段，前面是
// 公司與 UEN、後面是服務與序號；沒有服務尾段的舊快照也拆得開），公司名本身
// 含「 - 」也拆得對；認不得的字串原樣回傳（entity＝全字串），顯示層自然退化
// 成純文字。
export const splitEngagementName = (name) => {
  const p = parseEngagementName(name);
  return p.composed
    ? { entity: p.entity, uen: p.uen, fye: p.fye, service: p.service, seq: p.seq }
    : { entity: String(name || ''), uen: '', fye: '', service: '', seq: null };
};

export const shortEngagementName = (name) => splitEngagementName(name).entity;

// 同一頁並列多案（頁籤、Copy from、缺漏清單、部分失敗訊息）的辨識名。
// 公司名不重複就只用公司名；同公司多案時加一段 sub，取「服務（＋序號）·
// 描述第一行」（engagementTag，描述只有可多案的服務才有），分不開再加 FYE、
// 再加 ENG-xxx。
// items: engagement 列（slim list 或 amendment 列皆可）：{ id | engagementId,
// engagementName, serviceProvided?, engagementSeq?, serviceDescription?, yearEnd? }，
// 沒帶服務欄位的從組合名拆。
// 回傳 { [id]: { label, sub, text } }；text＝「公司 (sub)」給純文字句子用。
export const engagementLabelsOf = (items = []) => {
  const rows = items.filter(Boolean).map((e) => {
    const id = e.id !== undefined && e.id !== null ? e.id : e.engagementId;
    const p = splitEngagementName(e.engagementName || '');
    const service = e.serviceProvided !== undefined && e.serviceProvided !== null ? e.serviceProvided : p.service;
    const seq = e.engagementSeq !== undefined && e.engagementSeq !== null ? e.engagementSeq : p.seq;
    return {
      id,
      label: p.entity || e.entityName || `#${id}`,
      parts: [
        engagementTag(service, seq, e.serviceDescription),
        p.fye ? `FYE ${formatDateShort(p.fye.split('-').reverse().join('-'))}` : '',
        engagementNo(id),
      ],
    };
  });
  const groups = {};
  rows.forEach((r) => { (groups[r.label.toLowerCase()] = groups[r.label.toLowerCase()] || []).push(r); });
  const out = {};
  Object.values(groups).forEach((group) => {
    if (group.length === 1) {
      const r = group[0];
      out[r.id] = { label: r.label, sub: '', text: r.label };
      return;
    }
    // 先看服務＋描述；仍分不開才加 FYE、再加 ENG。
    const varies = (i) => new Set(group.map((r) => r.parts[i])).size > 1;
    const used = [];
    for (const step of [[0], [1], [2]]) {
      step.filter(varies).forEach((i) => used.push(i));
      const keys = group.map((r) => used.map((k) => r.parts[k]).join('|'));
      if (used.length > 0 && new Set(keys).size === group.length) break;
    }
    group.forEach((r) => {
      const sub = used.map((k) => r.parts[k]).filter(Boolean).join(' · ') || r.parts[0] || r.parts[2];
      out[r.id] = { label: r.label, sub, text: `${r.label} (${sub})` };
    });
  });
  return out;
};

// FSA/QC35A 的 engagementLabel「<組合名> (FYE 31 December 2025)」帶著跟組合名
// 重複的 FYE 括號——顯示前先剝掉，EngagementName 的淡色小字已經講了同一件事。
export const stripDupFyeLabel = (label) => {
  const m = /^(.*) \(FYE [^)]+\)$/.exec(String(label || ''));
  return m ? m[1] : String(label || '');
};

// 單一組合案名：公司名主體＋File Ref./UEN/FYE 淡色小字。`no` 是 engagement 的
// 檔案編號（ENG-305），只有拿得到 engagement id 的呼叫端傳得出來——從字串拆出
// 來的清單（History 差異、清單頁儲存格）沒有 id，那幾處的小字就只有 UEN/FYE。
// `now` 是 API 的 engagementNameNow（主檔改名後的現行組合名，沒改就是空）——
// 公司名後接灰字「(now: …)」，UEN 也變了就一起列（shared/NowName.js 同一規則）。
// `serviceDescription` 是 API 同名欄位（可多案服務的描述，其他服務為 null）——
// 服務（與序號）後接描述第一行（engagementTag），同公司同 FYE 的 AUP 與
// AUP #2 只靠它分得開。呼叫端一律傳，tests/api/47 掃原始碼把關。
export const EngagementName = ({ name, no = '', now = '', serviceDescription = '' }) => {
  const { entity, uen, fye, service, seq } = splitEngagementName(name);
  const meta = [no, uen, fye ? `FYE ${fye}` : '', engagementTag(service, seq, serviceDescription)].filter(Boolean).join(' · ');
  const live = now ? splitEngagementName(now) : null;
  const nowText = live && (live.entity !== entity || live.uen !== uen)
    ? [live.entity !== entity ? live.entity : '', live.uen !== uen ? live.uen : ''].filter(Boolean).join(' · ')
    : '';
  const nowNode = nowText ? <span className={nameNow} title={NOW_TITLE}>(now: {nowText})</span> : null;
  if (!meta) return <span className="break-words">{entity}{nowNode}</span>;
  return (
    <span className="inline-block break-words">
      {entity}{nowNode}
      <span className={coverageMeta}>{meta}</span>
    </span>
  );
};

// QC 記錄標籤（「QC7 · 公司 · FYE」只講公司與年結）＋該記錄涵蓋案的服務 tag
// 淡色小字——同公司同 FYE 可以有兩張 QC 記錄（各管一個 AUP 案），只靠 tag 分得開。
// items: [{ engagementName, serviceDescription }]（QCA／QCAD 清單的 recordEngagements）。
export const RecordLabelCell = ({ label, items = [] }) => {
  const tags = [...new Set(items.map((e) => {
    const { service, seq } = splitEngagementName(e.engagementName);
    return engagementTag(service, seq, e.serviceDescription);
  }).filter(Boolean))];
  return (
    <span className="break-words">
      {label}
      {tags.length > 0 && <span className={coverageMeta}>{tags.join('; ')}</span>}
    </span>
  );
};

// 清單列上的展開鈕——只切換展開，不觸發整列跳詳情頁。
export const ExpandToggle = ({ expanded, onToggle, children }) => (
  <button
    type="button"
    aria-expanded={expanded}
    className={rowExpandToggle}
    onClick={(ev) => { ev.stopPropagation(); onToggle(); }}
  >
    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    {children}
  </button>
);

// 涵蓋的案一案一列（公司＋ENG／UEN／FYE／服務／描述），各自連到 Engagement 頁
// （新分頁）：Records 清單展開列與 QC／FS／QC35 Records 詳情的 Engagements Covered。
export const CoveredEngagementsPanel = ({ items = [] }) => (
  <ul className={coverageList}>
    {items.map((e, i) => (
      <li key={`${e.engagementId || 'deleted'}-${i}`} className={coverageListRow}>
        {e.engagementId ? (
          <RefLink to={`/qm/engagements/${e.engagementId}`} className="text-blue-600 hover:text-blue-800">
            <EngagementName name={e.engagementName} no={engagementNo(e.engagementId)} serviceDescription={e.serviceDescription} />
          </RefLink>
        ) : (
          <span className="text-slate-500"><EngagementName name={e.engagementName} serviceDescription={e.serviceDescription} /> (deleted)</span>
        )}
      </li>
    ))}
  </ul>
);

// 三個 Records 清單（FS／QC35／QC6/7）的 expandRow：涵蓋兩案以上才可展開。
export const coverageExpandRow = (r) => (
  (r.engagements || []).length > 1 ? <CoveredEngagementsPanel items={r.engagements} /> : null
);

// 三個 Records 清單（FS／QC35／QC6/7）的 Engagement(s) 欄：每案印完整組合名
// （公司 · UEN · FYE · 服務 · 描述），公司／UEN／FYE 不另立欄——一筆記錄可涵蓋
// 多家公司、各案 FYE 日期也可不同，單一欄位只能印第一案、會讓人以為全部相同。
// 第一案完整印；涵蓋兩案以上多一顆「N engagements」展開鈕（展開列見 coverageExpandRow）。
export const CoverageCell = ({ record, ctx }) => {
  const list = record.engagements || [];
  if (list.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <span>
      <EngagementName name={list[0].engagementName} serviceDescription={list[0].serviceDescription} />
      {list.length > 1 && ctx && (
        <span className="block">
          <ExpandToggle expanded={ctx.expanded} onToggle={ctx.toggle}>{list.length} engagements</ExpandToggle>
        </span>
      )}
      {list.length > 1 && !ctx && <span className="text-slate-500"> +{list.length - 1}</span>}
    </span>
  );
};

export const AddedBadge = () => <span className={coverageBadgeAdded}>Added</span>;
export const RemovedBadge = () => <span className={coverageBadgeRemoved}>Removed</span>;

// 多筆一筆一列（外框同 SplitList）。items 可以是字串或帶 engagementName 的物件；badgeOf 回傳要
// 掛在該列尾巴的徽章（或 null）。
export const EngagementNameList = ({
  items = [],
  nameOf = (it) => (typeof it === 'string' ? it : (it.engagementName || it.entityName || '')),
  // 檔案編號只認 engagementId。涵蓋列自己的 `id` 是連結列的主鍵，不是
  // engagement 的——拿它當編號會印出別人的案號。
  noOf = (it) => (typeof it === 'string' ? '' : engagementNo(it.engagementId)),
  // 主檔改名後的現行名（API 的 *Now 欄位；字串項目沒有）。
  nowOf = (it) => (typeof it === 'string' ? '' : (it.engagementNameNow || it.entityNameNow || '')),
  // 該案的 serviceDescription（字串項目沒有）。
  descriptionOf = (it) => (typeof it === 'string' ? '' : (it.serviceDescription || '')),
  keyOf = null,
  badgeOf = null,
  className = '',
}) => {
  if (items.length === 0) return null;
  return (
    <ul className={`${coverageList} ${className}`}>
      {items.map((it, i) => {
        const badge = badgeOf ? badgeOf(it, i) : null;
        return (
          <li key={keyOf ? keyOf(it, i) : `${nameOf(it)}-${i}`} className={coverageListRow}>
            <EngagementName name={nameOf(it)} no={noOf ? noOf(it) : ''} now={nowOf ? nowOf(it) : ''} serviceDescription={descriptionOf ? descriptionOf(it) : ''} />
            {badge}
          </li>
        );
      })}
    </ul>
  );
};

// 伺服器串好的清單字串拆回一筆一筆：「 + 」（diff 字串）與「; 」（CWCC/EAR）
// 直接拆；「, 」串（清單端點的 string_agg）只在前一段已經是完整組合名
// （拆得出 UEN 與 FYE）時才切，公司名裡的逗號不會被切壞——認不得的字串整包回傳。
export const splitJoinedNames = (v) => {
  const s = String(v || '').trim();
  if (!s) return [];
  if (s.includes(' + ')) return s.split(' + ').map((t) => t.trim()).filter(Boolean);
  if (s.includes('; ')) return s.split('; ').map((t) => t.trim()).filter(Boolean);
  const segs = s.split(', ');
  const out = [];
  for (const seg of segs) {
    if (out.length > 0 && !parseEngagementName(out[out.length - 1]).composed) {
      out[out.length - 1] += `, ${seg}`;
    } else {
      out.push(seg.trim());
    }
  }
  return out.filter(Boolean);
};

// 差異表儲存格：把串好的字串就地拆成一筆一行。
export const CoverageValue = ({ value }) => {
  const items = splitJoinedNames(value);
  if (items.length === 0) return <>—</>;
  return <EngagementNameList items={items} />;
};

// History／ChangesSinceReturn 的 fmtChangeValue 包裝：清單型欄位改走
// CoverageValue（一筆一行），其他欄位交回頁面原本的 formatter。
const COVERAGE_FIELDS = new Set(['engagements', 'entities', 'engagementNames', 'files', 'addEntities']);
export const withCoverageFmt = (baseFmt) => (field, v) => (
  COVERAGE_FIELDS.has(field) && typeof v === 'string' && v !== ''
    ? <CoverageValue value={v} />
    : baseFmt(field, v)
);

// QCA（及同形 payload）的涵蓋差異：兩側都拆成清單，只在 Requested 側標
// Added、只在 Current 側標 Removed——比對用整個組合名字串，舊單（只存字串、
// 沒有 machine snapshot）也判得出來。
export const CoverageDiffValue = ({ before, after, side }) => {
  const beforeItems = splitJoinedNames(before);
  const afterItems = splitJoinedNames(after);
  const beforeSet = new Set(beforeItems);
  const afterSet = new Set(afterItems);
  if (side === 'before') {
    return (
      <EngagementNameList
        items={beforeItems}
        badgeOf={(n) => (afterSet.has(n) ? null : <RemovedBadge />)}
      />
    );
  }
  return (
    <EngagementNameList
      items={afterItems}
      badgeOf={(n) => (beforeSet.has(n) ? null : <AddedBadge />)}
    />
  );
};

// QCA / CAR 詳情頁預設的差異值 formatter（fmtAmendValue 的清單感知版）。
export const fmtAmendValueRich = withCoverageFmt(fmtAmendValue);

// 沒有自訂 formatter 的頁面用這顆：HistoryCard 預設 fmt 的清單感知版
// （附件差異「a.pdf + b.pdf」也是串好的字串，一樣一筆一行）。
export const fmtCoverageDefault = withCoverageFmt(
  (field, v) => (v === null || v === undefined || v === '' ? '—' : String(v))
);

// 清單頁儲存格：第一筆（公司名主體＋淡色 meta）＋「+N」，整串收進 title。
// `now` 是同一串以主檔現名重組的版本（API 的 *NamesNow，沒改就是空）——
// 第一筆若改了名就接「(now: …)」。
// `serviceDescription` 是第一案的（清單端點的 firstServiceDescription）。
export const JoinedNamesCell = ({ value, fallback = '', now = '', serviceDescription = '' }) => {
  const items = splitJoinedNames(value || fallback);
  if (items.length === 0) return <span className="text-slate-400">—</span>;
  const nowItems = now ? splitJoinedNames(now) : [];
  return (
    <span title={items.join(', ')}>
      <EngagementName name={items[0]} now={nowItems[0] || ''} serviceDescription={serviceDescription} />
      {items.length > 1 && <span className="text-slate-400"> +{items.length - 1}</span>}
    </span>
  );
};
